import React, { useEffect } from "react";
import { useToast } from "../../../component/common/Toast";
import { GoogleSignInButton } from "../components/GoogleSignInButton";

export const GoogleSheetUtil = ({
  sheetIds,
  setSheetIds,
  authToken,
  setAuthToken,
  isGoogleScriptLoaded,
  transactions,
  setTransactions,
  writing,
  setWriting,
  summaryData,
  setSummaryData,
  setBudgetEstimates,
}) => {
  const WORKBOOK_NAME = "DO_NOT_RENAME---MY_FULL_FINANCE_MASTER_BUDGET";
  const FIVE_MINUTES = 5 * 60 * 1000;
  const BUDGET_SHEET_NAME = "Budget";
  const SUMMARY_SHEET_NAME = "Summary";
  const TRANSACTIONS_SHEET_NAME = "Transactions";
  const { showToast } = useToast();

  useEffect(() => {
    const storedToken = localStorage.getItem("googleAuthToken");
    const storedSheetIds = localStorage.getItem("spreadsheetIds");

    if (storedToken) {
      const parsedToken = JSON.parse(storedToken);
      setAuthToken(parsedToken);
      showToast("Auth token set from local storage", "success");
      if (storedSheetIds && storedSheetIds.workbookId) {
        setSheetIds(JSON.parse(storedSheetIds));
        showToast("Sheet ids set from local storage", "success");
      }
    }
  }, []);

  //when the auth token is set, find or create the workbook and sheets
  useEffect(() => {
    if (isGoogleScriptLoaded) {
      if (authToken && !sheetIds.workbookId) {
        findOrCreateWorkbookAndSheets(authToken.access_token);
      } else {
        initializeTokenClient();
      }
    }
  }, [isGoogleScriptLoaded, authToken]);

  //when the sheet ids are set, load any existing transactions, summary, and budget
  useEffect(() => {
    if (!sheetIds.workbookId) return;

    if (transactions.length === 0) {
      loadTransactions();
      loadSummary();
      loadBudgetEstimates();
    }
  }, [sheetIds]);

  //when csv transaction files are uploaded, write them to the sheet
  useEffect(() => {
    if (writing) {
      writeTransactionsToSheet();
      showToast("New transactions uploaded", "success");
      setWriting(false);
    }
  }, [writing]);

  const handleAuthError = (error) => {
    showToast("Auth error: " + error.message, "error");
    // Close any open popup windows
    if (window.authPopup && !window.authPopup.closed) {
      window.authPopup.close();
    }
  };

  const initializeTokenClient = (force = false) => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      showToast(
        "Google Client ID not configured. Google Sheets integration disabled.",
        "warning"
      );
      return;
    }

    if (window.google) {
      try {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          scope: 'https://www.googleapis.com/auth/drive.file',
          callback: (response) => {
            handleTokenResponse(response);
          },
          error_callback: handleAuthError,
        });

        showToast("Requesting access token", "info");

        // Simple check for Safari
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent
        );

        if (!isSafari || force) {
          client.requestAccessToken();
        }
      } catch (error) {
        handleAuthError(error);
      }
    }
  };

  const handleTokenResponse = async (tokenResponse) => {
    if (tokenResponse.access_token) {
      const tokenWithExpiry = {
        ...tokenResponse,
        expiryTimestamp: Date.now() + tokenResponse.expires_in * 1000,
      };
      setAuthToken(tokenWithExpiry);
      localStorage.setItem("googleAuthToken", JSON.stringify(tokenWithExpiry));
      showToast("Auth token set", "success");
    }
  };

  const refreshTokenIfNeeded = () => {
    if (
      !authToken ||
      !authToken.expiryTimestamp ||
      Date.now() + FIVE_MINUTES >= authToken.expiryTimestamp
    ) {
      showToast("Refreshing token", "info");
      localStorage.removeItem("googleAuthToken");
      initializeTokenClient();
    }
  };

  const findOrCreateWorkbookAndSheets = async (accessToken) => {
    try {
      showToast("Searching for existing workbook", "info");
      const searchResponse = await fetch(
        `https://www.googleapis.com/drive/v3/files?q=name='${WORKBOOK_NAME}' and mimeType='application/vnd.google-apps.spreadsheet' and trashed=false`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const searchData = await searchResponse.json();
      let sheetsData, workbookId;
      if (searchData.files && searchData.files.length > 0) {
        showToast("Found existing workbook", "success");
        workbookId = searchData.files[0].id;

        // get the sheet ids in the workbook
        const sheetsResponse = await fetch(
          `https://sheets.googleapis.com/v4/spreadsheets/${workbookId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        sheetsData = await sheetsResponse.json();
      } else {
        showToast("Creating new workbook and sheets", "info");
        const createResponse = await fetch(
          "https://sheets.googleapis.com/v4/spreadsheets",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              properties: {
                title: WORKBOOK_NAME,
              },
              sheets: [
                {
                  properties: {
                    title: TRANSACTIONS_SHEET_NAME,
                  },
                },
                {
                  properties: {
                    title: SUMMARY_SHEET_NAME,
                  },
                },
                {
                  properties: {
                    title: BUDGET_SHEET_NAME,
                  },
                },
              ],
            }),
          }
        );
        sheetsData = await createResponse.json();
        workbookId = sheetsData.spreadsheetId;
        showToast("New workbook created", "success");
      }

      const transactionsSheetId = sheetsData.sheets.find(
        (sheet) => sheet.properties.title === TRANSACTIONS_SHEET_NAME
      );
      const summarySheetId = sheetsData.sheets.find(
        (sheet) => sheet.properties.title === SUMMARY_SHEET_NAME
      );
      const budgetSheetId = sheetsData.sheets.find(
        (sheet) => sheet.properties.title === BUDGET_SHEET_NAME
      );
      setSheetIds({
        workbookId: workbookId,
        transactionsSheetId: transactionsSheetId?.properties.sheetId,
        summarySheetId: summarySheetId?.properties.sheetId,
        budgetSheetId: budgetSheetId?.properties.sheetId,
      });
      localStorage.setItem("spreadsheetIds", JSON.stringify(sheetIds));
    } catch (err) {
      showToast("Error finding/creating spreadsheet: " + err.message, "error");
      refreshTokenIfNeeded();
      return null;
    }
  };

  const writeTransactionsToSheet = async () => {
    if (!authToken || !sheetIds.workbookId || !transactions.length) return;

    try {
      // Clear existing content in the Transactions, Summary, and Budget sheets
      await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetIds.workbookId}:batchUpdate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken.access_token}`,
          },
          body: JSON.stringify({
            requests: [
              {
                updateCells: {
                  range: {
                    sheetId: sheetIds.transactionsSheetId,
                  },
                  fields: "*",
                },
              },
              {
                updateCells: {
                  range: {
                    sheetId: sheetIds.summarySheetId,
                  },
                  fields: "*",
                },
              },
              {
                updateCells: {
                  range: {
                    sheetId: sheetIds.budgetSheetId,
                  },
                  fields: "*",
                },
              },
            ],
          }),
        }
      );
      console.log("cleared existing content");

      // // Format transactions for Google Sheets
      // const headers = [
      //   "Transaction Date",
      //   "Description",
      //   "Category",
      //   "Amount",
      //   "Account",
      // ];
      const values = [
        // headers,
        ...transactions.map((t) => [
          t.transactionDate,
          t.description,
          t.category,
          t.amount,
          t.account,
        ]),
      ];

      const maxColumnPosition = String.fromCharCode(
        65 + /*headers.length - 1*/ 5
      );
      const rowCount = values.length;
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetIds.workbookId}/values/Transactions!A1:${maxColumnPosition}${rowCount}?valueInputOption=USER_ENTERED`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken.access_token}`,
          },
          body: JSON.stringify({
            range: `Transactions!A1:${maxColumnPosition}${rowCount}`,
            values,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to write to spreadsheet");
      }
      console.log("wrote transactions to sheet");
      // Create pivot table after successful write
      await writePivotTableToSummarySheet();
    } catch (err) {
      showToast("Error writing to spreadsheet: " + err.message, "error");
      refreshTokenIfNeeded();
    }
  };

  const writePivotTableToSummarySheet = async () => {
    try {
      // Create the pivot table
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetIds.workbookId}:batchUpdate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken.access_token}`,
          },
          body: JSON.stringify({
            requests: [
              {
                updateCells: {
                  rows: [
                    {
                      values: [
                        {
                          pivotTable: {
                            source: {
                              sheetId: sheetIds.transactionsSheetId,
                              startRowIndex: 0,
                              endRowIndex: transactions.length,
                              startColumnIndex: 0,
                              endColumnIndex: 5,
                            },
                            rows: [
                              {
                                sourceColumnOffset: 2,
                                showTotals: true,
                                sortOrder: "ASCENDING",
                                valueBucket: {
                                  buckets: [],
                                  valuesIndex: 0,
                                },
                              },
                            ],
                            columns: [
                              {
                                sourceColumnOffset: 0,
                                groupRule: {
                                  dateTimeRule: {
                                    type: "YEAR_MONTH",
                                  },
                                },
                                showTotals: true,
                                sortOrder: "ASCENDING",
                              },
                            ],
                            values: [
                              {
                                sourceColumnOffset: 3,
                                summarizeFunction: "SUM",
                                name: "Total Amount",
                              },
                            ],
                            valueLayout: "HORIZONTAL",
                          },
                        },
                      ],
                    },
                  ],
                  start: {
                    sheetId: sheetIds.summarySheetId,
                    rowIndex: 0,
                    columnIndex: 0,
                  },
                  fields: "pivotTable",
                },
              },
            ],
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create pivot table");
      }

      loadSummary();

      await writeBudgetEstimatesToSheet();
    } catch (err) {
      showToast("Error creating pivot table: " + err.message, "error");
      refreshTokenIfNeeded();
    }
  };

  const writeBudgetEstimatesToSheet = async () => {
    try {
      // Find grand total rows and calculate estimates
      const estimates = []; // Using Map to store category -> estimate mapping
      let monthCount = 0;

      // Get month count from header row
      if (summaryData.length > 0) {
        monthCount = summaryData[1].length - 2; // Subtract category and grand total columns
      }

      // Calculate estimates
      summaryData.forEach((row) => {
        if (!row.some((cell) => cell.includes("Total"))) {
          const category = row[0];
          const grandTotal = parseFloat(row[row.length - 1]);
          if (!isNaN(grandTotal) && monthCount > 0) {
            const monthlyEstimate = grandTotal / monthCount;
            estimates.push([category, monthlyEstimate]);
          }
        }
      });

      // Prepare values for the budget sheet
      const columnValues = estimates;

      // Write estimates to the budget sheet
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetIds.workbookId}/values/${BUDGET_SHEET_NAME}!A1:B${columnValues.length}?valueInputOption=USER_ENTERED`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken.access_token}`,
          },
          body: JSON.stringify({
            range: `${BUDGET_SHEET_NAME}!A1:B${columnValues.length}`,
            values: columnValues,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to write budget estimates");
      }

      loadBudgetEstimates();
    } catch (err) {
      showToast("Error calculating budget estimates: " + err.message, "error");
      refreshTokenIfNeeded();
    }
  };

  const loadTransactions = async () => {
    try {
      const request = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetIds.workbookId}/values/${TRANSACTIONS_SHEET_NAME}!A1:E10000`,
        {
          headers: {
            Authorization: `Bearer ${authToken.access_token}`,
          },
        }
      );
      const responseData = await request.json();
      if (!responseData.values || responseData.values.length === 0) {
        return [];
      }
      const transactions = responseData.values.map((row) => {
        if (row.length === 0 || !row[0]) return null;
        return {
          transactionDate: row[0],
          description: row[1],
          category: row[2],
          amount: row[3],
          account: row[4],
        };
      });
      const data = transactions.filter((t) => t !== null);

      if (data.length === 0) {
        showToast("No transactions found", "warning");
        return;
      }
      setTransactions(data);
      showToast("Existing transactions loaded", "success");
    } catch (err) {
      showToast("Failed to fetch summary data: " + err.message, "error");
      refreshTokenIfNeeded();
    }
  };

  const loadSummary = async () => {
    // Fetch the summary data after creating the pivot table
    const summaryResponse = await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${sheetIds.workbookId}/values/${SUMMARY_SHEET_NAME}!A1:Z150`,
      {
        headers: {
          Authorization: `Bearer ${authToken.access_token}`,
        },
      }
    );

    const summaryResponseData = await summaryResponse.json();
    setSummaryData(summaryResponseData.values || []); // Set the summary data
  };

  const loadBudgetEstimates = async () => {
    const budgetResponse = await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${sheetIds.workbookId}/values/${BUDGET_SHEET_NAME}!A1:B150`,
      {
        headers: {
          Authorization: `Bearer ${authToken.access_token}`,
        },
      }
    );
    const budgetResponseData = await budgetResponse.json();
    if (!budgetResponseData.values || budgetResponseData.values.length === 0) {
      return [];
    }

    const loadedBudgetEstimates = budgetResponseData.values.map((row) => {
      if (row.length === 0 || !row[0]) return null;
      return {
        category: row[0],
        monthlyEstimate: row[1],
      };
    });
    setBudgetEstimates(loadedBudgetEstimates);
  };

  const handleLoadData = () => {
    initializeTokenClient(true);
  };

  if (!authToken || !sheetIds.workbookId) {
    return (
      <div style={{ padding: "20px" }}>
        {!authToken ? (
          <GoogleSignInButton onClick={handleLoadData} />
        ) : (
          <div>Loading spreadsheet...</div>
        )}
      </div>
    );
  }

  return null;
};
